import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { KeyRound, Search, Loader2, Trash2, Plus, BookOpen, Layers } from 'lucide-react';

/**
 * ManageAccessDialog — grant a non-Premium user access to specific stories
 * or entire series. This does NOT change the user's Premium status.
 * Series-level grants automatically cover all existing and future episodes.
 */
export default function ManageAccessDialog({ open, onOpenChange, subscriber, onDone }) {
  const [grants, setGrants] = useState([]);
  const [catalog, setCatalog] = useState({ stories: [], series: [] });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(null); // key of item currently being toggled

  const load = async () => {
    if (!subscriber?.id) return;
    setLoading(true);
    try {
      const [g, c] = await Promise.all([
        api.get(`/admin/users/${subscriber.id}/access`),
        api.get('/admin/access/searchable-content'),
      ]);
      setGrants(g.data.grants || []);
      setCatalog(c.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro a carregar dados.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSearch('');
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subscriber?.id]);

  const grantsKey = useMemo(() => {
    const s = new Set();
    for (const g of grants) s.add(`${g.target_type}:${g.target_id}`);
    return s;
  }, [grants]);

  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filter = (arr) => q ? arr.filter(x => (x.title || '').toLowerCase().includes(q)) : arr;
    return { series: filter(catalog.series), stories: filter(catalog.stories) };
  }, [search, catalog]);

  const isGranted = (type, id) => grantsKey.has(`${type}:${id}`);
  const grantOf = (type, id) => grants.find(g => g.target_type === type && g.target_id === id);

  const toggle = async (type, item) => {
    const key = `${type}:${item.id}`;
    setSaving(key);
    try {
      if (isGranted(type, item.id)) {
        const g = grantOf(type, item.id);
        await api.delete(`/admin/users/${subscriber.id}/access/${g.id}`);
        setGrants(prev => prev.filter(x => x.id !== g.id));
        toast.success('Acesso removido.');
      } else {
        const { data } = await api.post(`/admin/users/${subscriber.id}/access`, {
          target_type: type,
          target_id: item.id,
        });
        setGrants(prev => [...prev, data]);
        toast.success('Acesso concedido.');
      }
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro a atualizar acesso.');
    } finally {
      setSaving(null);
    }
  };

  const isPremiumActive = !!subscriber?.is_premium;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manage-access-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <KeyRound className="w-5 h-5 text-ruby" />
            Gerir Acesso — {subscriber?.name || subscriber?.email}
          </DialogTitle>
          <DialogDescription>
            Concede acesso manual a séries ou contos específicos. Não altera o estado Premium.
            Um acesso concedido a uma série cobre automaticamente todos os episódios (atuais e futuros).
          </DialogDescription>
        </DialogHeader>

        {/* Access status badge */}
        <div className="rounded-md border border-border bg-white/5 p-3">
          <p className="text-xs text-muted-foreground mb-1">Tipo de acesso atual</p>
          {isPremiumActive ? (
            <Badge className="bg-gold text-black">Premium — acesso total</Badge>
          ) : grants.length > 0 ? (
            <Badge className="bg-blue-500/20 text-blue-300 border border-blue-500/40">
              Acesso Específico — {grants.length} concessão(ões)
            </Badge>
          ) : (
            <Badge variant="outline" className="border-border text-muted-foreground">Gratuito</Badge>
          )}
          {isPremiumActive && (
            <p className="text-xs text-amber-400 mt-2">
              ⚠️ Este utilizador tem Premium ativo. Concessões específicas ficam registadas mas não têm efeito visível até o Premium expirar.
            </p>
          )}
        </div>

        {/* Current grants */}
        {grants.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-foreground">Conteúdo com acesso concedido</p>
            <div className="space-y-1.5">
              {grants.map(g => (
                <div key={g.id} className="flex items-center justify-between gap-2 p-2 rounded-md bg-white/5" data-testid={`grant-row-${g.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {g.target_type === 'series' ? <Layers className="w-4 h-4 text-ruby shrink-0" /> : <BookOpen className="w-4 h-4 text-blue-400 shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm text-foreground truncate">{g.title || '(sem título)'}</p>
                      <p className="text-xs text-muted-foreground">
                        {g.target_type === 'series' ? 'Série completa' : 'Conto único'} · por {g.granted_by}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggle(g.target_type, { id: g.target_id })}
                    disabled={saving === `${g.target_type}:${g.target_id}`}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 shrink-0"
                    data-testid={`revoke-grant-${g.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content picker */}
        <div className="space-y-3 pt-2 border-t border-border">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-foreground">Adicionar acesso</p>
            <div className="flex-1" />
            <div className="relative w-full max-w-xs">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Pesquisar…"
                className="pl-8 bg-background border-border"
                data-testid="access-search"
              />
            </div>
          </div>

          {loading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-ruby" /></div>}

          {!loading && (
            <>
              {/* Series first — most common case */}
              {filteredCatalog.series.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Séries ({filteredCatalog.series.length})</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {filteredCatalog.series.map(item => {
                      const granted = isGranted('series', item.id);
                      const busy = saving === `series:${item.id}`;
                      return (
                        <button
                          key={item.id}
                          onClick={() => toggle('series', item)}
                          disabled={busy}
                          data-testid={`toggle-series-${item.id}`}
                          className={`flex items-center gap-2 p-2 rounded-md border text-left transition-colors ${
                            granted
                              ? 'bg-emerald-500/10 border-emerald-500/40'
                              : 'bg-white/5 border-border hover:bg-white/10'
                          }`}
                        >
                          <Layers className={`w-4 h-4 shrink-0 ${granted ? 'text-emerald-400' : 'text-muted-foreground'}`} />
                          <span className="flex-1 text-sm text-foreground truncate">{item.title}</span>
                          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (granted ? <span className="text-xs text-emerald-400">✓ Acesso</span> : <Plus className="w-3.5 h-3.5 text-muted-foreground" />)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {filteredCatalog.stories.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2 mt-3">Contos Premium ({filteredCatalog.stories.length})</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                    {filteredCatalog.stories.map(item => {
                      const granted = isGranted('story', item.id);
                      const busy = saving === `story:${item.id}`;
                      return (
                        <button
                          key={item.id}
                          onClick={() => toggle('story', item)}
                          disabled={busy}
                          data-testid={`toggle-story-${item.id}`}
                          className={`flex items-center gap-2 p-2 rounded-md border text-left transition-colors ${
                            granted
                              ? 'bg-emerald-500/10 border-emerald-500/40'
                              : 'bg-white/5 border-border hover:bg-white/10'
                          }`}
                        >
                          <BookOpen className={`w-4 h-4 shrink-0 ${granted ? 'text-emerald-400' : 'text-muted-foreground'}`} />
                          <span className="flex-1 text-sm text-foreground truncate">{item.title}</span>
                          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (granted ? <span className="text-xs text-emerald-400">✓</span> : <Plus className="w-3.5 h-3.5 text-muted-foreground" />)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {filteredCatalog.series.length === 0 && filteredCatalog.stories.length === 0 && search && (
                <p className="text-sm text-muted-foreground text-center py-6">Sem resultados para &quot;{search}&quot;.</p>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
